import { ref, type Ref } from 'vue'

import type { ApolloClientLike } from '@/shared/ports/resolveClient'
import {
  RaidMapTreePositionSetDocument,
  type RaidMapTreePositionSetMutation,
} from '../../lib/handWrittenGraphql.ts'
import type { RaidId, RaidMapTreePosition } from '../../types/index.ts'
import type { RaidMapCtx } from './map.ts'

export interface RaidMapTreePositionSnapshot {
  readonly xBasisPoints: number
  readonly yBasisPoints: number
}

export interface RaidMapTreePositionApi {
  readonly treePosition: Ref<RaidMapTreePosition | null>
  readonly editGeneration: () => number
  readonly applyTreePosition: (position: RaidMapTreePositionSnapshot | null) => void
  readonly clearTreePosition: () => void
  readonly setRaidMapTreePosition: (
    xBasisPoints: number,
    yBasisPoints: number,
  ) => Promise<void>
}

interface QueuedTreePosition extends RaidMapTreePosition {
  readonly editGeneration: number
}

interface PositionWorker {
  readonly scopeGeneration: number
  readonly promise: Promise<void>
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error)
}

function validBasisPoints(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 10_000
}

/**
 * Keeps the movable Tree objective in the same scoped, coalesced write model
 * as team markers without making the main map store exceed its file budget.
 */
export function useRaidMapTreePosition(
  ctx: RaidMapCtx,
  client: ApolloClientLike,
  error: Ref<string | null>,
): RaidMapTreePositionApi {
  const treePosition = ref<RaidMapTreePosition | null>(null)
  let scopeGeneration = 0
  let latestEditGeneration = 0
  let queuedPosition: QueuedTreePosition | null = null
  let worker: PositionWorker | null = null

  function isCurrentScope(
    requestScope: number,
    guildId: string,
    raidId: RaidId,
  ): boolean {
    return requestScope === scopeGeneration
      && ctx.activeDiscordGuildId.value === guildId
      && ctx.activeRaidId.value === raidId
  }

  function applyTreePosition(position: RaidMapTreePositionSnapshot | null): void {
    treePosition.value = position === null
      ? null
      : {
        xBasisPoints: position.xBasisPoints,
        yBasisPoints: position.yBasisPoints,
      }
  }

  function clearTreePosition(): void {
    scopeGeneration += 1
    latestEditGeneration += 1
    queuedPosition = null
    treePosition.value = null
  }

  function activeRaidOrError(): RaidId | null {
    const raidId = ctx.activeRaidId.value
    if (raidId === null) error.value = 'Select a raid before editing its map.'
    return raidId
  }

  function reconcileFailure(
    requestScope: number,
    guildId: string,
    raidId: RaidId,
    editGeneration: number,
    message: string,
  ): void {
    if (
      editGeneration === latestEditGeneration
      && isCurrentScope(requestScope, guildId, raidId)
    ) {
      error.value = message
    }
  }

  async function drainPositionQueue(
    requestScope: number,
    guildId: string,
    raidId: RaidId,
  ): Promise<void> {
    while (isCurrentScope(requestScope, guildId, raidId)) {
      const position = queuedPosition
      if (position === null) return
      queuedPosition = null
      try {
        const response = await client.mutate<RaidMapTreePositionSetMutation>({
          mutation: RaidMapTreePositionSetDocument,
          variables: {
            guildId,
            raidId,
            xBasisPoints: position.xBasisPoints,
            yBasisPoints: position.yBasisPoints,
          },
        })
        if (!isCurrentScope(requestScope, guildId, raidId)) return
        if (position.editGeneration !== latestEditGeneration) continue
        if (response.data?.raidMapTreePositionSet !== true) {
          reconcileFailure(
            requestScope,
            guildId,
            raidId,
            position.editGeneration,
            'The Tree position could not be saved.',
          )
        }
      } catch (caught: unknown) {
        reconcileFailure(
          requestScope,
          guildId,
          raidId,
          position.editGeneration,
          errorMessage(caught),
        )
      }
    }
  }

  async function setRaidMapTreePosition(
    xBasisPoints: number,
    yBasisPoints: number,
  ): Promise<void> {
    const raidId = activeRaidOrError()
    if (raidId === null) return
    if (!validBasisPoints(xBasisPoints) || !validBasisPoints(yBasisPoints)) {
      error.value = 'Map coordinates must be whole basis points from 0 to 10000.'
      return
    }
    const guildId = ctx.activeDiscordGuildId.value
    const requestScope = scopeGeneration
    const position: QueuedTreePosition = {
      xBasisPoints,
      yBasisPoints,
      editGeneration: ++latestEditGeneration,
    }
    error.value = null
    treePosition.value = {
      xBasisPoints: position.xBasisPoints,
      yBasisPoints: position.yBasisPoints,
    }
    queuedPosition = position
    if (worker?.scopeGeneration !== requestScope) {
      const promise = drainPositionQueue(requestScope, guildId, raidId)
        .finally(() => {
          if (worker?.scopeGeneration === requestScope) worker = null
        })
      worker = {
        scopeGeneration: requestScope,
        promise,
      }
    }
    await worker.promise
  }

  return {
    treePosition,
    editGeneration: () => latestEditGeneration,
    applyTreePosition,
    clearTreePosition,
    setRaidMapTreePosition,
  }
}
